import { useEffect, useState, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Share2, Check, Twitter } from 'lucide-react';
import LearnPage from '@/pages/learn/LearnPage';
import { dividendStatus, PENDING_DECLARATION_LABEL } from '@/lib/dividendEligibility';
import { WarrantPanel } from '@/components/WarrantBadge';
import UnitPanel from '@/components/UnitPanel';
import RightPanel, { isSpacRight, rightShareTicker, rightName } from '@/components/RightPanel';
import { useWarrant } from '@/hooks/useWarrant';
import CompanyDetailEnrichment from '@/components/CompanyDetailEnrichment';
import StockMetricsTab from '@/components/StockMetricsTab';
import CloRiskReturnBlock from '@/components/CloRiskReturnBlock';
import MmfSeoMetrics from '@/components/MmfSeoMetrics';
import useDividendSchedule from '@/hooks/useDividendSchedule';
import FixedRateAssetCard, { isFixedRateAsset } from '@/components/FixedRateAssetCard';
import AdrWhtEstimatorCard from '@/components/AdrWhtEstimatorCard';
import SplitNote from '@/components/SplitNote';
import { DeclaredPill, EstimateDisclaimer } from '@/components/DeclaredPill';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Round P146·4 (Jun 2026) — "Listed Aug 2026" pill next to the H1 for
// IPOs under 90 days old. `list_date` comes from the Massive-first
// profile overlay (`_overlay_massive_profile`), with the lazily-cached
// `massive_list_date` (branding enrichment) as fallback. Narrower
// window than the modal's 24-month RecentlyListedBadge — the SEO page
// only flags genuinely brand-new listings.
function NewListingBadge({ listDate }) {
  if (!listDate) return null;
  const iso = String(listDate).slice(0, 10);
  const listed = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(listed.getTime())) return null;
  const ageDays = (Date.now() - listed.getTime()) / 86400000;
  if (ageDays < 0 || ageDays >= 90) return null;
  const label = listed.toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
  return (
    <span
      className="inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold tracking-wide border bg-sky-500/10 border-sky-500/40 text-sky-700 dark:text-sky-300"
      data-testid="stock-seo-new-listing-badge"
      title={`IPO'd ${iso} — 52-week range, SMA200 and streak metrics are still thin for new listings`}
    >
      Listed {label}
    </span>
  );
}

/**
 * StockSeoPage — Round 91n (Feb 2026), non-div adapted Feb 26 2026.
 *
 * Public SEO page for any individual ticker. Lives at `/stock/:ticker`
 * and reuses the LearnPage chrome + useSeoMeta hook from the Learn
 * module so titles/descriptions/OG/JSON-LD all flow through the same
 * tested path.
 *
 * The page is **type-adaptive**: it auto-detects whether the ticker
 * pays a dividend (via `stock.pays_dividend`) and renders the
 * appropriate hero block:
 *   - Dividend payers → Dividend snapshot (yield, freq, ex-div, CAGR,
 *     recent payments) + Dividend Triangle cross-link.
 *   - Non-dividend payers (NFLX, BRK.B, etc.) → Capital snapshot
 *     (price, market cap, Capital Quality Rank, ROIC, revenue
 *     CAGR, shareholder yield, Altman Z zone). NO dividend tables,
 *     NO Triangle link, NO DRIP/JEPI/SCHD related cards.
 *
 * The URL, canonical, and route shape are unchanged so Google's
 * existing 6k+ indexed pages keep ranking — only the body content +
 * meta titles/descriptions diverge by ticker type.
 */
export default function StockSeoPage() {
  const { ticker: rawTicker } = useParams();
  const ticker = (rawTicker || '').toUpperCase();
  const navigate = useNavigate();
  const [stock, setStock] = useState(null);
  const { data: schedule } = useDividendSchedule(ticker);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Round 95p·8 (Feb 2026) — Related tickers, powered by
  // Massive's /v1/related-companies endpoint. Cached 24h server-side.
  // Feb 2026 — mode branches "dividend" vs "non_dividend" based on
  // the source ticker, so a non-div page never surfaces div peers.
  const [related, setRelated] = useState([]);
  const [relatedMode, setRelatedMode] = useState('dividend');
  // Round P140·3 (Feb 2026) — Warrant lookup for the SEO page. When
  // the ticker is a warrant, we render the WarrantPanel above the
  // dividend/non-dividend body so search-engine visitors AND real
  // users see the underlying, exercise price, CIK, and any active
  // redemption event. Silent 404 when the ticker isn't a warrant.
  const { warrant } = useWarrant(ticker);

  useEffect(() => {
    if (!ticker) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setStock(null);
    setRelated([]);
    setRelatedMode('dividend');
    axios
      .get(`${API}/stocks/${encodeURIComponent(ticker)}`)
      .then((r) => { if (!cancelled) setStock(r.data); })
      .catch((e) => {
        if (!cancelled) setError(e?.response?.status === 404 ? 'not-found' : 'fetch-failed');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    // Fetch related in parallel — non-blocking so the main content
    // never waits on this.
    axios
      .get(`${API}/stocks/${encodeURIComponent(ticker)}/related`, { params: { limit: 4 } })
      .then((r) => {
        if (cancelled) return;
        setRelated(r.data?.results || []);
        setRelatedMode(r.data?.mode || 'dividend');
      })
      .catch(() => { /* soft-fail — hide the section */ });
    return () => { cancelled = true; };
  }, [ticker]);

  // P158·9 (Feb 2026) — MMFs (3 letters + XX) get their own copy
  // section and drop the Triangle cross-link. Prefer the backend
  // flag; fall back to the ticker shape so an un-seeded MMF still
  // classifies correctly on the SEO page.
  const isMoneyMarketFund = !!stock?.is_money_market_fund
    || /^[A-Z]{3}XX$/.test(String(ticker || '').toUpperCase());
  // Sep 2026 — Ordinary open-end mutual funds (5 letters, SINGLE
  // trailing X: VTSAX, FXAIX). No data source we use carries their
  // distributions, so `pays_dividend` swings on whichever inference
  // job touched the row last — the page asserted "a non-dividend-paying
  // stock" about a fund that pays quarterly income, and the other
  // branch would render a dividend page full of blanks. Same failure
  // mode as the money market demotion: no data read as no dividend.
  // Route them to the non-dividend body, which has an explicit
  // "distributions not tracked" treatment instead of a claim.
  const isMutualFund = !isMoneyMarketFund && (
    String(stock?.quote_type || '').toUpperCase() === 'MUTUALFUND'
    || !!stock?.is_mutual_fund
    || /^[A-Z]{4}X$/.test(String(ticker || '').toUpperCase())
  );
  // Round 95p·10 — Central helper: treat REIT/BDC IPOs as
  // dividend-eligible even before their first declaration.
  const status = dividendStatus(stock);
  const isDividendStock = status !== 'non_dividend' && !isMutualFund;
  const isPendingDeclaration = status === 'eligible_pending' && !isMutualFund;

  // ── Shared meta ───────────────────────────────────────────────
  const stockName = stock?.name || ticker;
  const stockSector = stock?.sector || stock?.fund_category || null;
  const price = typeof stock?.price === 'number' ? stock.price : null;
  const marketCap = typeof stock?.market_cap === 'number' ? stock.market_cap : null;

  // ── Dividend-only fields — Massive schedule first, stock_cache fallback ──
  const yieldPct = typeof stock?.dividend_yield === 'number' ? stock.dividend_yield : null;
  const divRate = typeof stock?.dividend_rate === 'number' ? stock.dividend_rate : null;
  const sched = schedule?.has_history ? schedule : null;
  const freq = sched?.frequency || stock?.frequency || null;
  const exDate = sched?.next?.ex_date_human || stock?.next_ex_date || stock?.ex_dividend_date || null;
  const payDate = sched?.last?.pay_date_human || stock?.last_payment_date || null;
  const lastAmount = sched?.last?.amount ?? stock?.last_dividend_value ?? null;
  const nextPayment = sched?.next || null;
  const history = !isDividendStock ? []
    : sched ? sched.history.slice(0, 8).map((h) => ({ date: h.ex_date, amount: h.amount })).reverse()
      : Array.isArray(stock?.dividend_history_24m) ? stock.dividend_history_24m.slice(-8) : [];

  // ── Non-dividend (Capital Analytics) fields ───────────────────
  const eq = stock?.equity_quality || null;
  const capRank = typeof eq?.rank === 'number' ? eq.rank : null;
  const capVerdictLabel = eq?.verdict_label || null;
  const roic = typeof eq?.roic === 'number' ? eq.roic : null;
  const revCagr3y = typeof eq?.revenue_cagr_3y === 'number' ? eq.revenue_cagr_3y : null;
  const buybackYield = typeof eq?.buyback_yield_raw === 'number' ? eq.buyback_yield_raw : null;
  const altmanZone = eq?.altman_zone || null;

  // ── SEO title + description, branched by stock type ───────────
  const seoTitle = isMutualFund
    ? (stock
        ? `${ticker} Mutual Fund — ${stockName} NAV & Net Assets (2026) | DiviDrip`
        : `${ticker} Mutual Fund — NAV & Net Assets | DiviDrip`)
    : isDividendStock
    ? (stock
        ? `${ticker} Stock — ${stockName} Dividend, Yield & Ex-Div Date (2026) | DiviDrip`
        : `${ticker} Stock — Dividend, Yield & Ex-Div Date | DiviDrip`)
    : (stock
        ? `${ticker} Stock — ${stockName} Price, Sector & Capital Quality Rank (2026) | DiviDrip`
        : `${ticker} Stock — Price, Sector & Capital Quality | DiviDrip`);

  const seoDescription = isMutualFund
    ? (stock
        ? `${ticker} (${stockName}) is an open-end mutual fund${price != null ? `, NAV $${price.toFixed(2)}` : ''}. DiviDrip does not track mutual fund distributions — see how five-letter single-X mutual fund tickers work and where to find the fund's own distribution history.`
        : `${ticker} is an open-end mutual fund. DiviDrip does not track mutual fund distributions.`)
    : isDividendStock
    ? (stock
        ? `${ticker} (${stockName}) ${yieldPct != null ? `dividend yield ${yieldPct.toFixed(2)}%` : 'dividend yield'}${divRate != null ? `, $${divRate.toFixed(2)} annual` : ''}${freq ? `, ${freq.toLowerCase()} payments` : ''}. Free dividend tracker with live price, ex-div date, pay date, and payout history.`
        : `Track ${ticker} dividends, yield, ex-div date, pay date, and growth history — free dividend portfolio tracker.`)
    : (stock
        ? `${ticker} (${stockName})${price != null ? ` trading at $${price.toFixed(2)}` : ''}${stockSector ? `, ${stockSector} sector` : ''}${capRank != null ? `. Capital Quality Rank ${capRank.toFixed(0)}/100${capVerdictLabel ? ` — ${capVerdictLabel}` : ''}` : ''}. Free stock tracker with market cap, ROIC, revenue growth, and shareholder yield.`
        : `Track ${ticker} price, market cap, ROIC, revenue growth, and Capital Quality Rank — free non-dividend stock tracker.`);

  // ── JSON-LD structured data ───────────────────────────────────
  const jsonLd = stock ? {
    '@context': 'https://schema.org',
    '@type': 'FinancialProduct',
    name: `${ticker} — ${stockName}`,
    description: stock?.description?.slice(0, 300) || seoDescription,
    url: `https://twylightcrow.com/stock/${ticker}`,
    ...(stock?.exchange ? {
      identifier: { '@type': 'PropertyValue', propertyID: 'tickerSymbol', value: ticker },
    } : {}),
    ...(isDividendStock
      ? (yieldPct != null ? {
          additionalProperty: [
            { '@type': 'PropertyValue', name: 'dividendYield', value: `${yieldPct.toFixed(2)}%` },
            ...(divRate != null ? [{ '@type': 'PropertyValue', name: 'annualDividend', value: `$${divRate.toFixed(2)}` }] : []),
            ...(freq ? [{ '@type': 'PropertyValue', name: 'paymentFrequency', value: freq }] : []),
            ...(exDate ? [{ '@type': 'PropertyValue', name: 'nextExDividendDate', value: exDate }] : []),
          ],
        } : {})
      : {
          additionalProperty: [
            ...(price != null ? [{ '@type': 'PropertyValue', name: 'price', value: `$${price.toFixed(2)}` }] : []),
            ...(marketCap != null ? [{ '@type': 'PropertyValue', name: 'marketCap', value: fmtMarketCap(marketCap) }] : []),
            ...(stockSector ? [{ '@type': 'PropertyValue', name: 'sector', value: stockSector }] : []),
            ...(capRank != null ? [{ '@type': 'PropertyValue', name: 'capitalQualityRank', value: `${capRank.toFixed(0)}/100` }] : []),
            ...(roic != null ? [{ '@type': 'PropertyValue', name: 'roic', value: `${(roic * 100).toFixed(1)}%` }] : []),
          ],
        }
    ),
  } : null;

  // ── Round 95p·6 (Feb 2026) — BreadcrumbList + FAQPage structured data.
  // BreadcrumbList tells Google to render `Home › Stocks › TICKER` in the
  // search result. FAQPage exposes concise Q&A the search snippet can
  // pull dividend yield / next ex-div date / current price from.
  const canonicalUrl = `https://twylightcrow.com/stock/${ticker}`;

  // Round 95p·7 — Ex-div countdown chip. Renders "today", "in 3 days",
  // or "2 days ago" next to the date so it feels alive. Falls back
  // to null when the string can't be parsed.
  const exDateCountdown = useMemo(() => {
    if (!exDate) return null;
    const d = new Date(exDate);
    if (Number.isNaN(d.getTime())) return null;
    // Compare at day granularity so timezones don't wobble the count.
    const today = new Date();
    const toDay = (x) => Date.UTC(x.getUTCFullYear(), x.getUTCMonth(), x.getUTCDate());
    const days = Math.round((toDay(d) - toDay(today)) / 86400000);
    if (days === 0) return { label: 'today', tone: 'urgent' };
    if (days === 1) return { label: 'tomorrow', tone: 'urgent' };
    if (days === -1) return { label: 'yesterday', tone: 'past' };
    if (days > 0) return { label: `in ${days} days`, tone: days <= 7 ? 'soon' : 'future' };
    return { label: `${Math.abs(days)} days ago`, tone: 'past' };
  }, [exDate]);

  // Q&A used for BOTH the FAQPage JSON-LD and the visible "Frequently
  // asked" section further down the page. Single source of truth.
  const faqItems = useMemo(() => {
    if (!stock) return [];
    const qa = [];
    if (isDividendStock) {
      if (yieldPct != null) {
        qa.push({
          q: `What is ${ticker}'s dividend yield?`,
          a: `${ticker} (${stockName}) currently has a dividend yield of ${yieldPct.toFixed(2)}%${divRate != null ? `, which works out to about $${divRate.toFixed(2)} in annual dividend payments per share` : ''}.`,
        });
      }
      if (exDate) {
        // Round P145 — Daily payers ex-date reads awkwardly as
        // "in 1 day" every single day, so replace with the mechanics
        // note. Preserved for weekly/monthly/quarterly.
        const exAnswer = freq === 'Daily'
          ? `${ticker} goes ex-dividend every US business day — its next ex-date is ${exDate}, and every following business day (Mon-Fri, excluding NYSE holidays) is another ex-date. Own the shares at market close and you're entitled to that day's cash payout the next business day.`
          : `${ticker}'s next ex-dividend date is ${exDate}${exDateCountdown ? ` (${exDateCountdown.label})` : ''}. To receive the upcoming dividend, you must own shares before this date.`;
        qa.push({
          q: `When is ${ticker}'s next ex-dividend date?`,
          a: exAnswer,
        });
      }
      if (payDate) {
        qa.push({
          q: `When did ${ticker} last pay a dividend?`,
          a: `${ticker}'s most recent dividend payment date was ${payDate}.`,
        });
      }
      if (freq) {
        // Round P145 (Feb 2026) — Daily payers get a bespoke sentence.
        // SATA became the first-ever stock to pay every business day
        // (launched June 16 2026 by Strive Inc.), and the generic
        // "SATA pays dividends on a daily schedule" reads awkward.
        // The rewrite calls out the ~252 payouts/year and the
        // historic-first framing for SEO snippet value.
        const answer = freq === 'Daily'
          ? `${ticker} pays dividends every US business day (~252 payouts a year, T+1 settlement) — the first stock ever to move to a daily cash-dividend cadence. Own shares by market close and you're entitled to the next business day's payout.`
          : `${ticker} pays dividends on a ${freq.toLowerCase()} schedule.`;
        qa.push({
          q: `How often does ${ticker} pay dividends?`,
          a: answer,
        });
      }
      // Round 95p·8 — CAGR question. Prefer 3-year CAGR; fall back
      // to 5-year, then 2-year (whichever the ticker's cache has).
      const cagr3 = typeof stock?.dividend_cagr_3y === 'number' ? stock.dividend_cagr_3y : null;
      const cagr5 = typeof stock?.dividend_cagr_5y === 'number' ? stock.dividend_cagr_5y : null;
      const cagr2 = typeof stock?.dividend_cagr_2y === 'number' ? stock.dividend_cagr_2y : null;
      let cagrValue = null; let cagrLabel = null;
      if (cagr3 != null) { cagrValue = cagr3; cagrLabel = '3-year'; }
      else if (cagr5 != null) { cagrValue = cagr5; cagrLabel = '5-year'; }
      else if (cagr2 != null) { cagrValue = cagr2; cagrLabel = '2-year'; }
      if (cagrValue != null) {
        const pct = Number(cagrValue).toFixed(2);
        const direction = cagrValue > 0
          ? `growing its dividend`
          : cagrValue < 0
            ? `shrinking its dividend`
            : `holding its dividend flat`;
        qa.push({
          q: `How fast is ${ticker} growing its dividend?`,
          a: `${ticker} has been ${direction} at a ${pct}% ${cagrLabel} compound annual growth rate (CAGR). CAGR smooths year-to-year noise into a single average growth rate.`,
        });
      }
    } else {
      if (price != null) {
        qa.push({
          q: `What is ${ticker}'s current stock price?`,
          a: `${ticker} (${stockName}) is currently trading at $${price.toFixed(2)}.`,
        });
      }
      qa.push(isMutualFund ? {
        q: `Does ${ticker} pay a dividend?`,
        a: `${ticker} (${stockName}) is an open-end mutual fund. Most stock and bond mutual funds pay income distributions (commonly quarterly or annually) plus a capital gains distribution near year end, but DiviDrip does not track mutual fund distributions — our data sources do not carry them. Check the fund's own distribution history at the fund family or on your brokerage statement. This page shows price and net assets only.`,
      } : {
        q: `Does ${ticker} pay a dividend?`,
        a: `${ticker} (${stockName}) does not currently pay a regular dividend.`,
      });
      // Round P153 (Sep 2026) — SPAC FAQ entries for blank-check shells.
      if (stock.is_spac) {
        qa.push({
          q: `Is ${ticker} a SPAC?`,
          a: `Yes — ${ticker} (${stockName}) is a Special Purpose Acquisition Company (blank-check shell). It holds its IPO cash in a trust account (typically ~$10 per share) while searching for a private company to merge with. SPACs pay no dividends.`,
        });
        qa.push({
          q: `What happens when ${ticker} completes its merger?`,
          a: `When a SPAC closes its business combination (the "de-SPAC"), shareholders' shares convert into shares of the combined company and the ticker changes. Before the merger vote, shareholders can redeem shares for their pro-rata slice of the trust account. If no deal closes by the deadline, the SPAC liquidates and returns the trust cash.`,
        });
      }
      if (capRank != null) {
        qa.push({
          q: `What is ${ticker}'s Capital Quality Rank?`,
          a: `${ticker} has a Capital Quality Rank of ${capRank.toFixed(0)}/100${capVerdictLabel ? ` — ${capVerdictLabel}` : ''}. This score blends ROIC, revenue growth, buybacks, and balance-sheet health.`,
        });
      }
    }
    return qa;
  }, [stock, ticker, stockName, isDividendStock, isMutualFund, yieldPct, divRate, freq, exDate, exDateCountdown, payDate, price, capRank, capVerdictLabel]);

  const breadcrumbLd = stock ? {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://twylightcrow.com/' },
      { '@type': 'ListItem', position: 2, name: 'Stocks', item: 'https://twylightcrow.com/screener' },
      { '@type': 'ListItem', position: 3, name: ticker, item: canonicalUrl },
    ],
  } : null;

  const faqLd = faqItems.length > 0 ? {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  } : null;

  // Inject the two extra JSON-LD blocks alongside the FinancialProduct
  // one (which LearnPage already emits via the `jsonLd` prop). Kept
  // separate so we can drop them cleanly on route change.
  useEffect(() => {
    const scripts = [];
    for (const [ld, tag] of [
      [breadcrumbLd, 'stock-breadcrumb'],
      [faqLd, 'stock-faq'],
    ]) {
      if (!ld) continue;
      const s = document.createElement('script');
      s.type = 'application/ld+json';
      s.text = JSON.stringify(ld);
      s.setAttribute(`data-${tag}`, '1');
      document.head.appendChild(s);
      scripts.push(s);
    }
    return () => { scripts.forEach(s => s.remove()); };
  }, [breadcrumbLd, faqLd]);

  // ── Share pill ─────────────────────────────────────────────────
  const [copied, setCopied] = useState(false);
  const shareSnippet = useMemo(() => {
    if (!stock) return '';
    const parts = [`${ticker} (${stockName})`];
    if (isDividendStock) {
      if (yieldPct != null) parts.push(`yield ${yieldPct.toFixed(2)}%`);
      if (divRate != null) parts.push(`$${divRate.toFixed(2)} annual`);
      if (freq) parts.push(freq.toLowerCase());
      if (exDate) parts.push(`next ex-div ${exDate}`);
    } else {
      if (price != null) parts.push(`$${price.toFixed(2)}`);
      if (stockSector) parts.push(stockSector);
      if (capRank != null) parts.push(`Quality Rank ${capRank.toFixed(0)}/100`);
      if (capVerdictLabel) parts.push(capVerdictLabel);
    }
    return `${parts.join(' · ')} — full breakdown: ${canonicalUrl}`;
  }, [stock, ticker, stockName, isDividendStock, yieldPct, divRate, freq, exDate, price, stockSector, capRank, capVerdictLabel, canonicalUrl]);

  const handleShareCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareSnippet);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch {
      // Fallback: select + execCommand-style copy via temporary textarea
      const ta = document.createElement('textarea');
      ta.value = shareSnippet;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); setCopied(true); setTimeout(() => setCopied(false), 2200); } catch { /* noop */ }
      document.body.removeChild(ta);
    }
  };

  const handleShareToX = () => {
    if (!shareSnippet) return;
    const intent = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareSnippet)}`;
    window.open(intent, '_blank', 'noopener,noreferrer');
  };

  const sharePill = stock ? (
    <span className="inline-flex items-center gap-2 align-middle">
      <NewListingBadge listDate={stock.list_date || stock.massive_list_date} />
      <button
        type="button"
        onClick={handleShareCopy}
        data-testid="stock-seo-share-pill"
        title={copied ? 'Copied!' : 'Copy ticker snippet + link'}
        className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold tracking-wide border transition-colors ${
          copied
            ? 'bg-emerald-500/15 border-emerald-500/50 text-emerald-700 dark:text-emerald-300'
            : 'bg-amber-500/10 border-amber-500/40 text-amber-700 dark:text-amber-300 hover:bg-amber-500/20 hover:border-amber-500/60'
        }`}
      >
        {copied ? <Check className="w-3.5 h-3.5" /> : <Share2 className="w-3.5 h-3.5" />}
        {copied ? 'Copied' : 'Copy'}
      </button>
      <button
        type="button"
        onClick={handleShareToX}
        data-testid="stock-seo-share-x-pill"
        title="Share on X — opens a pre-composed post"
        className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold tracking-wide border bg-sky-500/10 border-sky-500/40 text-sky-700 dark:text-sky-300 hover:bg-sky-500/20 hover:border-sky-500/60 transition-colors"
      >
        <Twitter className="w-3.5 h-3.5" />
        Share on X
      </button>
    </span>
  ) : null;

  // ── Related links — branched by stock type ────────────────────
  // Dividend stocks keep the Triangle/JEPI/SCHD/DRIP cross-links.
  // Non-dividend stocks hide every dividend-specific link
  // (Triangle, JEPI vs JEPQ, SCHD vs VOO, DRIP) until we ship the
  // non-div guide pack (separate roadmap item).
  const relatedLinks = isMoneyMarketFund ? [
    // P158·9 — Money Market Fund related links. Drops the Triangle
    // cross-link (Triangle math doesn't apply to a constant-NAV
    // product) and swaps in guides that make sense for a cash-sweep
    // vehicle.
    { to: '/screener', label: 'Stock Screener — filter by asset class, yield, dividend safety' },
    { to: '/learn/what-is-drip', label: 'What is DRIP? Reinvestment explained' },
    { to: '/how-to', label: `How to add ${ticker} to your portfolio` },
  ] : isDividendStock ? [
    { to: `/triangle/${ticker}`, label: `${ticker} Dividend Triangle — Revenue × EPS × Dividend health` },
    { to: '/learn/jepi-vs-jepq', label: 'JEPI vs JEPQ — covered-call yield face-off' },
    { to: '/learn/schd-vs-voo', label: 'SCHD vs VOO — dividend ETF vs total-market ETF' },
    { to: '/learn/what-is-drip', label: 'What is DRIP? Reinvestment explained' },
    { to: '/screener', label: 'Stock Screener — find more like this' },
    { to: '/how-to', label: 'How to track this ticker in your portfolio' },
  ] : [
    { to: '/screener', label: 'Stock Screener — filter by sector, market cap, fundamentals' },
    { to: '/learn/how-to-screen-for-dividend-stocks', label: 'How to use the Screener — sector / yield / quality filters' },
    { to: '/learn/cost-basis-methods-fifo-lifo-hifo', label: 'Cost basis methods — FIFO, LIFO, average cost' },
    { to: '/learn/tax-loss-harvesting-playbook', label: 'Tax-loss harvesting — wash-sale rules explained' },
    { to: '/how-to', label: `How to add ${ticker} to your watchlist` },
  ];

  return (
    <LearnPage
      title={seoTitle}
      description={seoDescription}
      canonical={`https://twylightcrow.com/stock/${ticker}`}
      kicker={`DiviDrip · ${ticker}`}
      h1={loading ? `Loading ${ticker}…` : (error === 'not-found' ? `${ticker} — not found` : `${ticker} — ${stockName}`)}
      titleAdornment={sharePill}
      jsonLd={jsonLd}
      related={relatedLinks}
    >
      {loading && (
        <p className="flex items-center gap-2 text-muted-foreground my-6" data-testid="stock-seo-loading">
          <Loader2 className="w-4 h-4 animate-spin" /> Fetching latest {ticker} data…
        </p>
      )}

      {!loading && error === 'not-found' && (
        <>
          <p>
            We couldn&apos;t find a ticker matching <strong>{ticker}</strong> in our universe.
            DiviDrip covers 4,598+ US-listed dividend payers — if your symbol isn&apos;t here,
            double-check the spelling, or try the <Link to="/screener">Screener</Link> to
            browse by yield / sector / payout frequency.
          </p>
          <p>
            Looking for a known ticker? Try <Link to="/stock/JEPI">JEPI</Link>,
            <Link to="/stock/SCHD"> SCHD</Link>, <Link to="/stock/JNJ"> JNJ</Link>,
            <Link to="/stock/O"> O</Link>, or <Link to="/stock/KO"> KO</Link>.
          </p>
        </>
      )}

      {!loading && stock && isDividendStock && (
        <>
          {/* P158·9 (Feb 2026) — Money Market Fund explainer block.
              Renders ABOVE the standard DividendBody so search users
              landing on FGXXX / SPAXX / VMFXX / etc. immediately see
              "this is an MMF, not a stock" and understand the $1.00
              NAV convention BEFORE the dividend cadence numbers.
              Also carries the ticker convention explainer so users
              can generalise (double-XX = MMF, single X = mutual fund). */}
          {isMoneyMarketFund && (
            <section
              className="not-prose my-6 rounded-lg border-2 border-cyan-500/50 bg-cyan-50/50 dark:bg-cyan-950/20 p-4 sm:p-5 space-y-3"
              data-testid="mmf-seo-section"
            >
              <h2 className="text-lg sm:text-xl font-bold text-cyan-800 dark:text-cyan-200 !mt-0">
                {ticker} is a Money Market Fund
              </h2>
              {/* Oct 2026 — same tiles as the /money-market-fund-yields
                  table (family, type, expense, weekly liquid, as-of) plus
                  a link to the hub. */}
              <MmfSeoMetrics stock={stock} />
              <p className="text-sm sm:text-base leading-relaxed !my-0">
                <strong>{ticker}</strong> ({stockName}) is a{' '}
                <strong>Money Market Fund (MMF)</strong> — not an individual
                stock and not an ETF. Money Market Funds are the class of
                mutual funds that <strong>aim to maintain a constant $1.00
                Net Asset Value (NAV)</strong> at all times. Instead of
                trading live on an exchange like a stock, they price once
                per day at their closing NAV and pay their earnings out as
                regular dividend distributions.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <div>
                  <h3 className="font-semibold text-cyan-800 dark:text-cyan-200 !mt-0 !mb-1">What&apos;s inside</h3>
                  <p className="!my-0 leading-relaxed text-muted-foreground">
                    Short-dated U.S. Treasury bills, repurchase agreements
                    collateralised by Treasuries, and other highest-credit-
                    quality government or corporate paper. Average portfolio
                    maturity is typically 30–60 days, keeping interest-rate
                    risk minimal.
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold text-cyan-800 dark:text-cyan-200 !mt-0 !mb-1">Where you&apos;ll see it</h3>
                  <p className="!my-0 leading-relaxed text-muted-foreground">
                    Brokerages (Fidelity, Schwab, Vanguard, First American,
                    BlackRock, Federated Hermes) use MMFs as the automatic
                    <strong> cash-sweep</strong> vehicle for uninvested cash
                    balances so idle cash earns the short-rate yield.
                  </p>
                </div>
              </div>
              <p className="text-xs !my-0 leading-relaxed p-2.5 rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-300/60 dark:border-amber-700/40 text-amber-900 dark:text-amber-100">
                <strong>Aims to maintain $1.00 — not guaranteed.</strong> SEC
                rule 2a-7 governs Money Market Funds, but a fund can still
                &ldquo;break the buck&rdquo; in extreme market stress. MMFs
                are not FDIC-insured; SIPC covers brokerage custody, not
                fund credit risk. Read the prospectus before treating any
                MMF as cash-equivalent.
              </p>
              <p className="text-xs text-muted-foreground !my-0 leading-relaxed">
                <strong>Ticker convention:</strong> US mutual funds always have
                exactly 5-letter tickers. Money Market Funds specifically end
                in <strong>double-XX</strong> (SPAXX, FGXXX, VMFXX, SWVXX);
                ordinary open-end mutual funds end in a single X (VTSAX, FXAIX);
                individual stocks are 1–4 letters and ETFs are 3–4 letters.
                DiviDrip auto-classifies any 3-letter-prefix + XX ticker as an
                MMF, hides the Dividend Triangle tab (which values dividend
                growth — not applicable to a $1.00-NAV product), and swaps in
                this overview instead.
              </p>
            </section>
          )}
          {/* Round P140·3 (Feb 2026) — WarrantPanel on SEO route. Silent
              null-return when the ticker isn't a warrant.
              Round P140·5 — Pass the fetched stock price so the P/L
              Calculator can compute intrinsic + break-even without a
              second lookup. */}
          {warrant && warrant.is_unit && (
            <UnitPanel warrant={warrant} onOpenTicker={(t) => navigate(`/stock/${encodeURIComponent(t)}`)} />
          )}
          {!warrant && isSpacRight(stock) && (
            <RightPanel ticker={ticker} name={rightName(stock)} cik={stock?.cik} shareTicker={rightShareTicker(ticker)} onOpenTicker={(t) => navigate(`/stock/${encodeURIComponent(t)}`)} />
          )}
          {warrant && !warrant.is_unit && (
            <WarrantPanel
              warrant={warrant}
              currentPrice={stock?.price ?? stock?.current_price ?? null}
              onOpenTicker={(t) => navigate(`/stock/${encodeURIComponent(t)}`)}
            />
          )}
          <DividendBody
          ticker={ticker}
          stock={stock}
          stockName={stockName}
          stockSector={stockSector}
          price={price}
          yieldPct={yieldPct}
          divRate={divRate}
          freq={freq}
          exDate={exDate}
          exDateCountdown={exDateCountdown}
          payDate={payDate}
          lastAmount={lastAmount}
          nextPayment={nextPayment}
          history={history}
          splits={schedule?.splits}
          faqItems={faqItems}
          related={related}
          relatedMode={relatedMode}
          isPendingDeclaration={isPendingDeclaration}
          isMoneyMarketFund={isMoneyMarketFund}
        />
        </>
      )}

      {!loading && stock && !isDividendStock && (
        <>
          {/* Round P140·3 (Feb 2026) — WarrantPanel on SEO route (non-
              dividend branch — warrants land here because pays_dividend
              = false). Renders ABOVE the capital snapshot. */}
          {warrant && warrant.is_unit && (
            <UnitPanel warrant={warrant} onOpenTicker={(t) => navigate(`/stock/${encodeURIComponent(t)}`)} />
          )}
          {!warrant && isSpacRight(stock) && (
            <RightPanel ticker={ticker} name={rightName(stock)} cik={stock?.cik} shareTicker={rightShareTicker(ticker)} onOpenTicker={(t) => navigate(`/stock/${encodeURIComponent(t)}`)} />
          )}
          {warrant && !warrant.is_unit && (
            <WarrantPanel
              warrant={warrant}
              currentPrice={stock?.price ?? stock?.current_price ?? null}
              onOpenTicker={(t) => navigate(`/stock/${encodeURIComponent(t)}`)}
            />
          )}
          <NonDividendBody
          ticker={ticker}
          stock={stock}
          stockName={stockName}
          stockSector={stockSector}
          price={price}
          marketCap={marketCap}
          isMutualFund={isMutualFund}
          capRank={capRank}
          capVerdictLabel={capVerdictLabel}
          roic={roic}
          revCagr3y={revCagr3y}
          buybackYield={buybackYield}
          altmanZone={altmanZone}
          faqItems={faqItems}
          related={related}
          relatedMode={relatedMode}
        />
        </>
      )}

      {!loading && error === 'fetch-failed' && (
        <p className="text-rose-600 dark:text-rose-400">
          We hit a snag loading {ticker}. Try again in a minute, or{' '}
          <Link to="/screener">browse the Screener</Link>.
        </p>
      )}
    </LearnPage>
  );
}

// ── Dividend body (legacy layout, preserved verbatim for SEO continuity) ──
function DividendBody({ ticker, stock, stockName, stockSector, price, yieldPct, divRate, freq, exDate, exDateCountdown, payDate, lastAmount, nextPayment, history, splits, faqItems, related, relatedMode, isPendingDeclaration, isMoneyMarketFund }) {
  // Round 95p·10 — Pre-declaration REIT/BDC IPOs render this body
  // (via isDividendEligible) but have null yield/rate/dates. Show a
  // clear "Awaiting first declaration" cell so users don't misread
  // the em-dashes as "not a dividend stock."
  const pendingCell = isPendingDeclaration
    ? <span className="text-amber-600 dark:text-amber-300 font-medium">{PENDING_DECLARATION_LABEL}</span>
    : '—';
  const exDateCell = exDate ? (
    <span>
      {exDate}
      {/* Round P145 (Feb 2026) — Daily payers get the "next business day"
          pill instead of a stale "in 34 days" countdown that no longer
          makes sense once every business day is its own ex-date. */}
      {freq === 'Daily' ? (
        <span
          className="ml-2 text-xs font-medium text-emerald-600 dark:text-emerald-300"
          data-testid="stock-seo-exdiv-countdown"
        >
          (next business day)
        </span>
      ) : exDateCountdown && (
        <span
          className={`ml-2 text-xs font-medium ${
            exDateCountdown.tone === 'urgent'
              ? 'text-amber-600 dark:text-amber-300'
              : exDateCountdown.tone === 'soon'
                ? 'text-emerald-600 dark:text-emerald-300'
                : exDateCountdown.tone === 'past'
                  ? 'text-muted-foreground'
                  : 'text-primary'
          }`}
          data-testid="stock-seo-exdiv-countdown"
        >
          ({exDateCountdown.label})
        </span>
      )}
    </span>
  ) : '—';
  return (
    <>
      <p>
        <strong>{ticker}</strong> is{' '}
        {stock.fund_family ? `${stock.fund_family}'s ` : ''}
        <em>{stockName}</em>
        {stockSector ? ` (${stockSector})` : ''}.
        {price != null && ` Trading at $${price.toFixed(2)}.`}
        {isPendingDeclaration
          ? ` As a ${stock.is_reit ? 'REIT' : 'BDC'}, ${ticker} is required to distribute at least 90% of taxable income to shareholders — first dividend declaration is pending.`
          : yieldPct != null && ` Current dividend yield: ${yieldPct.toFixed(2)}%.`}
      </p>

      <h2>Dividend snapshot</h2>
      <table className="w-full text-sm my-4 border-collapse" data-testid="stock-seo-dividend-snapshot">
        <tbody>
          <Row label="Annual dividend rate" value={divRate != null ? `$${divRate.toFixed(2)}` : pendingCell} />
          <Row label="Current yield" value={yieldPct != null ? `${yieldPct.toFixed(2)}%` : pendingCell} />
          <Row label="Payment frequency" value={freq || pendingCell} />
          <Row label={<span className="inline-flex items-center gap-1.5">Next ex-dividend date{nextPayment && <DeclaredPill declared={nextPayment.declared} testId="stock-seo-exdiv-status" />}</span>} value={exDate ? exDateCell : pendingCell} />
          {nextPayment && (
            <Row
              label={<span className="inline-flex items-center gap-1.5">Next payment<DeclaredPill declared={nextPayment.declared} testId="stock-seo-nextpay-status" /></span>}
              value={<span data-testid="stock-seo-next-payment">{nextPayment.pay_date_human} · ${Number(nextPayment.amount).toFixed(4)}</span>}
            />
          )}
          <Row label="Most recent payment date" value={payDate || pendingCell} />
          <Row label="Last payment amount" value={lastAmount != null ? `$${Number(lastAmount).toFixed(4)}` : pendingCell} />
          <Row label="5-year dividend CAGR" value={stock.dividend_cagr_5y != null ? `${Number(stock.dividend_cagr_5y).toFixed(2)}%` : '—'} />
          <Row label="3-year dividend CAGR" value={stock.dividend_cagr_3y != null ? `${Number(stock.dividend_cagr_3y).toFixed(2)}%` : '—'} />
          {stock.expense_ratio != null && (
            <Row label="Expense ratio" value={`${Number(stock.expense_ratio).toFixed(2)}%`} />
          )}
          {stock.payout_ratio != null && !isFixedRateAsset(stock) && (
            <Row label="Payout ratio" value={`${Number(stock.payout_ratio).toFixed(0)}%`} />
          )}
          {stock.market_cap != null && (
            <Row label="Market cap" value={fmtMarketCap(stock.market_cap)} />
          )}
          {stock.exchange && (
            <Row label="Exchange" value={stock.exchange} />
          )}
        </tbody>
      </table>
      {nextPayment && !nextPayment.declared && (
        <div className="not-prose my-3 rounded-lg border border-amber-200 dark:border-amber-800/50 bg-amber-50/70 dark:bg-amber-950/20 px-3 py-2">
          <EstimateDisclaimer next={nextPayment} testId="stock-seo-estimate-disclaimer" />
        </div>
      )}

      {/* Oct 2026 — same Asset Class card as the Stock Modal Insights tab
          (par, coupon, cumulative status, admin-confirmed call terms). */}
      {isFixedRateAsset(stock) && (
        <div className="not-prose my-4" data-testid="stock-seo-fixed-rate-card">
          <FixedRateAssetCard stock={stock} />
        </div>
      )}

      {/* Oct 2026 — same ADR withholding + IRA-suitability card as the Stock
          Modal Insights tab and the /foreign-dividend-stocks hub. */}
      {stock?.is_adr && (
        <div className="not-prose my-4" data-testid="stock-seo-adr-card">
          <AdrWhtEstimatorCard stock={stock} testIdPrefix="seo-adr" />
        </div>
      )}

      {history.length > 0 && (
        <>
          <h2>Recent dividend payments</h2>
          <SplitNote
            splits={splits}
            since={history.reduce((m, p) => { const d = p.date || p.ex_dividend_date; return d && (!m || d < m) ? d : m; }, null)}
            suffix="— amounts in this table are split-adjusted"
            testId="stock-seo-split-note"
            className="mt-2"
          />
          <table className="w-full text-sm my-4 border-collapse" data-testid="stock-seo-history">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-2 pr-3 font-semibold">Date</th>
                <th className="text-right py-2 font-semibold">Amount / share</th>
              </tr>
            </thead>
            <tbody>
              {history.slice().reverse().map((p, i) => (
                <tr key={i} className="border-b border-border/40">
                  <td className="py-2 pr-3 font-mono text-xs">{p.date || p.ex_dividend_date || '—'}</td>
                  <td className="py-2 text-right font-mono">{typeof p.amount === 'number' ? `$${p.amount.toFixed(4)}` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      <h2>Price chart &amp; technicals</h2>
      <div className="not-prose my-4" data-testid="stock-seo-price-chart">
        <StockMetricsTab ticker={ticker} stock={stock} chartOnly showValuation={false} />
      </div>

      {stock.description && (
        <>
          <h2>About {ticker}</h2>
          <p>{stock.description}</p>
        </>
      )}
      {/* Round P146·4 — Listed since · Employees · HQ · Website chips
          (Massive branding data, same strip as the stock modal). The
          component self-fetches and renders nothing when Massive has
          no data, so it's safe on every ticker. */}
      <CompanyDetailEnrichment ticker={ticker} sector={stockSector} industry={stock.industry} />

      <RelatedTickers ticker={ticker} items={related} mode={relatedMode} />

      {/* Sep 2026 — CLO ETF section. Only rendered for detected CLO
          tickers (is_clo flag from backend). Explains the tranche the
          fund sits on, floating-rate mechanics, and Fed-rate
          sensitivity in prose (SEO-friendly), then embeds the same
          risk/return block the Stock Modal shows: 1y/2y/3y annualized
          total return, volatility, Sharpe, plus the sister-fund
          side-by-side. The block itself carries the "Show the math"
          panel with the three formulas. Rendered in both dividend and
          non-dividend bodies so reinvesting wrappers (RAAR, RCLR) and
          annual-pay wrappers (RAAY, RCLY) also get the section. */}
      {stock.is_clo && (
        <section data-testid="stock-seo-clo-section">
          <h2>What kind of ETF is {ticker}?</h2>
          <p>
            <strong>{ticker}</strong> is a Collateralized Loan Obligation (CLO) ETF — a fund that
            holds slices of securitized pools of senior-secured leveraged corporate loans. The
            underlying loans pay a floating interest rate (SOFR plus a spread), so when the Federal
            Reserve raises rates the fund&apos;s income rises with them; when the Fed cuts, income
            drifts down. Unlike traditional fixed-rate bond funds, CLOs carry almost no interest-rate
            duration — their prices don&apos;t rise when rates fall, so total return here is driven by
            the coupon stream rather than by capital gains.
          </p>
          <p>
            CLOs pay their investors down a waterfall. The AAA senior tranches are paid first and
            insulated by every tranche below them. The unrated equity slice is paid last and absorbs
            the first defaults. Between them sit the mezzanine tranches — AA, A, BBB, BB, B — which
            earn a higher coupon than AAA in exchange for taking losses earlier in a stress scenario.
            Where <strong>{ticker}</strong> sits on that waterfall determines the tradeoff between
            its yield and its price volatility.
          </p>

          <h3>{ticker} 3-year risk &amp; return</h3>
          <p>
            The table below shows <strong>{ticker}</strong>&apos;s annualized total return (share-price
            change plus reinvested dividends), annualized volatility (standard deviation of daily
            returns × √252), and Sharpe ratio (excess return per unit of volatility, using the
            current 3-year US Treasury yield as the risk-free rate). Numbers are computed from
            Massive.com daily closes and our own dividend records — when a CLO issuer offers both a
            AAA and a mezzanine fund, the sister fund appears alongside for a direct comparison.
          </p>
          <div className="not-prose my-4">
            <CloRiskReturnBlock ticker={ticker} />
          </div>

          <h3>How Fed rate moves affect {ticker}</h3>
          <p>
            Because the loans inside the CLO pool are floating-rate, <strong>{ticker}</strong>&apos;s
            income stream follows the front end of the yield curve within a quarter of any Fed move.
            A rate cut compresses the distribution (or slows NAV compounding, for the reinvesting
            wrappers) but simultaneously eases the interest burden on the leveraged corporate
            borrowers whose loans back the fund — reducing default risk, especially for BBB / B
            mezzanine tranches that would take the first hit in a downturn. A rate hike does the
            opposite: bigger monthly checks, but higher stress on the underlying borrowers.
          </p>
          <p>
            You can read the full breakdown of CLO tranches, the yield pick-up between AAA and BBB,
            and the Reckoner &ldquo;nesting-doll&rdquo; funds that reinvest CLO income instead of paying it
            out in the DiviDrip Learn guide:{' '}
            <Link to="/learn/clo-etfs-nesting-doll">
              CLO ETFs — The Waterfall, the Yield Pick-Up, and the Nesting-Doll Funds
            </Link>.
          </p>
        </section>
      )}

      <FaqSection items={faqItems} />

      <h2>Track {ticker} in DiviDrip</h2>
      <p>
        Add <strong>{ticker}</strong> to your portfolio on{' '}
        <Link to="/dashboard">DiviDrip</Link> to track every buy, DRIP reinvestment, and dividend
        payment in one place. Free, no credit card, no upsell — built by dividend
        investors, for dividend investors.
      </p>
      <ul>
        <li><Link to="/dashboard">Open DiviDrip</Link> — search <strong>{ticker}</strong> and add your shares.</li>
        {/* P158·9 (Feb 2026) — Skip Triangle for MMFs (constant NAV,
            math doesn't apply). SEO section above already explains
            what a Money Market Fund is. */}
        {!isMoneyMarketFund && (
          <li><Link to={`/triangle/${ticker}`}>View the {ticker} Dividend Triangle</Link> — see Revenue × EPS × Dividend growth at a glance.</li>
        )}
        <li><Link to="/screener">Stock Screener</Link> — find more dividend payers like {ticker}.</li>
        <li><Link to="/learn">DiviDrip Learn</Link> — free guides on DRIP, dividend tiers, ETF comparisons.</li>
      </ul>

      <p className="text-xs text-muted-foreground italic mt-6">
        Data refreshed {stock.fetched_at?.slice(0, 10) || 'recently'}. Not investment
        advice. Yields and prices change continuously; always verify the latest numbers
        in the live app before buying.
      </p>
    </>
  );
}

// ── Non-dividend body (Capital Analytics-focused) ────────────────
function NonDividendBody({ ticker, stock, stockName, stockSector, price, marketCap, isMutualFund, capRank, capVerdictLabel, roic, revCagr3y, buybackYield, altmanZone, faqItems, related, relatedMode }) {
  return (
    <>
      <p>
        <strong>{ticker}</strong> is{' '}
        <em>{stockName}</em>
        {stockSector ? ` (${stockSector})` : ''}
        {isMutualFund ? ' — an open-end mutual fund.' : ' — a non-dividend-paying stock.'}
        {price != null && ` ${isMutualFund ? 'NAV' : 'Trading'} at $${price.toFixed(2)}.`}
        {marketCap != null && ` ${isMutualFund ? 'Net assets' : 'Market cap'}: ${fmtMarketCap(marketCap)}.`}
        {!isMutualFund && capRank != null && ` Capital Quality Rank: ${capRank.toFixed(0)}/100${capVerdictLabel ? ` — ${capVerdictLabel}` : ''}.`}
      </p>

      {isMutualFund && (
        <p className="text-sm">
          <strong>DiviDrip does not track mutual fund distributions.</strong> Most
          stock and bond mutual funds pay an income distribution (commonly
          quarterly) plus a capital gains distribution near year end &mdash; our
          data sources do not carry those figures for single-X mutual fund
          tickers, so this page deliberately makes no claim about {ticker}&apos;s
          payouts. Read{' '}
          <Link to="/learn/mutual-fund-tickers">the single-X mutual fund
          guide</Link> for how the structure works, and check the fund family or
          your brokerage statement for its actual distribution history. The
          money market funds we do cover in full are on{' '}
          <Link to="/money-market-fund-yields">the money market fund yields
          page</Link>.
        </p>
      )}

      <h2>{isMutualFund ? 'Fund snapshot' : 'Stock snapshot'}</h2>
      <table className="w-full text-sm my-4 border-collapse" data-testid="stock-seo-nondiv-snapshot">
        <tbody>
          <Row label={isMutualFund ? 'NAV' : 'Price'} value={price != null ? `$${price.toFixed(2)}` : '—'} />
          <Row label={isMutualFund ? 'Net assets' : 'Market cap'} value={marketCap != null ? fmtMarketCap(marketCap) : '—'} />
          {!isMutualFund && <Row label="Sector" value={stockSector || '—'} />}
          <Row label="Exchange" value={stock.exchange || '—'} />
          <Row
            label={isMutualFund ? 'Distributions' : 'Pays dividend'}
            value={isMutualFund ? 'Not tracked' : 'No'}
          />
        </tbody>
      </table>

      {(stock.is_spac || stock.despac_predecessor) && <SpacSeoSection ticker={ticker} stockName={stockName} />}

      {(capRank != null || roic != null || revCagr3y != null || buybackYield != null || altmanZone) && (
        <>
          <h2>Capital Quality &amp; fundamentals</h2>
          <p className="text-sm text-muted-foreground">
            DiviDrip&apos;s Capital Analytics engine ranks every non-dividend stock 1-100
            on a 4-factor composite (Shareholder Yield 30%, Cash-Flow ROI 25%,
            Operating Momentum 25%, Tail-Risk 20%) — sector-neutralized so a
            biotech isn&apos;t penalized for negative earnings the way a utility would be.
          </p>
          <table className="w-full text-sm my-4 border-collapse" data-testid="stock-seo-capital-quality">
            <tbody>
              {capRank != null && (
                <Row label="Capital Quality Rank (1-100)" value={`${capRank.toFixed(0)} / 100${capVerdictLabel ? ` · ${capVerdictLabel}` : ''}`} />
              )}
              {roic != null && (
                <Row label="ROIC (TTM)" value={`${(roic * 100).toFixed(1)}%`} />
              )}
              {revCagr3y != null && (
                <Row label="Revenue CAGR (3y)" value={`${(revCagr3y * 100).toFixed(1)}%`} />
              )}
              {buybackYield != null && (
                <Row label="Shareholder buyback yield" value={`${(buybackYield * 100).toFixed(2)}%`} />
              )}
              {altmanZone && (
                <Row label="Altman Z-score zone" value={altmanZone} />
              )}
            </tbody>
          </table>
        </>
      )}

      <h2>Valuation, price chart &amp; technicals</h2>
      <div className="not-prose my-4" data-testid="stock-seo-price-chart">
        <StockMetricsTab ticker={ticker} stock={stock} chartOnly showValuation />
      </div>

      {stock.description && (
        <>
          <h2>About {ticker}</h2>
          <p>{stock.description}</p>
        </>
      )}
      {/* Round P146·4 — Listed since · Employees · HQ · Website chips
          (Massive branding data, same strip as the stock modal). The
          component self-fetches and renders nothing when Massive has
          no data, so it's safe on every ticker. */}
      <CompanyDetailEnrichment ticker={ticker} sector={stockSector} industry={stock.industry} />

      <RelatedTickers ticker={ticker} items={related} mode={relatedMode} />

      {/* Sep 2026 — CLO ETF section. Only rendered for detected CLO
          tickers (is_clo flag from backend). Explains the tranche the
          fund sits on, floating-rate mechanics, and Fed-rate
          sensitivity in prose (SEO-friendly), then embeds the same
          risk/return block the Stock Modal shows: 1y/2y/3y annualized
          total return, volatility, Sharpe, plus the sister-fund
          side-by-side. The block itself carries the "Show the math"
          panel with the three formulas. Rendered in both dividend and
          non-dividend bodies so reinvesting wrappers (RAAR, RCLR) and
          annual-pay wrappers (RAAY, RCLY) also get the section. */}
      {stock.is_clo && (
        <section data-testid="stock-seo-clo-section">
          <h2>What kind of ETF is {ticker}?</h2>
          <p>
            <strong>{ticker}</strong> is a Collateralized Loan Obligation (CLO) ETF — a fund that
            holds slices of securitized pools of senior-secured leveraged corporate loans. The
            underlying loans pay a floating interest rate (SOFR plus a spread), so when the Federal
            Reserve raises rates the fund&apos;s income rises with them; when the Fed cuts, income
            drifts down. Unlike traditional fixed-rate bond funds, CLOs carry almost no interest-rate
            duration — their prices don&apos;t rise when rates fall, so total return here is driven by
            the coupon stream rather than by capital gains.
          </p>
          <p>
            CLOs pay their investors down a waterfall. The AAA senior tranches are paid first and
            insulated by every tranche below them. The unrated equity slice is paid last and absorbs
            the first defaults. Between them sit the mezzanine tranches — AA, A, BBB, BB, B — which
            earn a higher coupon than AAA in exchange for taking losses earlier in a stress scenario.
            Where <strong>{ticker}</strong> sits on that waterfall determines the tradeoff between
            its yield and its price volatility.
          </p>

          <h3>{ticker} 3-year risk &amp; return</h3>
          <p>
            The table below shows <strong>{ticker}</strong>&apos;s annualized total return (share-price
            change plus reinvested dividends), annualized volatility (standard deviation of daily
            returns × √252), and Sharpe ratio (excess return per unit of volatility, using the
            current 3-year US Treasury yield as the risk-free rate). Numbers are computed from
            Massive.com daily closes and our own dividend records — when a CLO issuer offers both a
            AAA and a mezzanine fund, the sister fund appears alongside for a direct comparison.
          </p>
          <div className="not-prose my-4">
            <CloRiskReturnBlock ticker={ticker} />
          </div>

          <h3>How Fed rate moves affect {ticker}</h3>
          <p>
            Because the loans inside the CLO pool are floating-rate, <strong>{ticker}</strong>&apos;s
            income stream follows the front end of the yield curve within a quarter of any Fed move.
            A rate cut compresses the distribution (or slows NAV compounding, for the reinvesting
            wrappers) but simultaneously eases the interest burden on the leveraged corporate
            borrowers whose loans back the fund — reducing default risk, especially for BBB / B
            mezzanine tranches that would take the first hit in a downturn. A rate hike does the
            opposite: bigger monthly checks, but higher stress on the underlying borrowers.
          </p>
          <p>
            You can read the full breakdown of CLO tranches, the yield pick-up between AAA and BBB,
            and the Reckoner &ldquo;nesting-doll&rdquo; funds that reinvest CLO income instead of paying it
            out in the DiviDrip Learn guide:{' '}
            <Link to="/learn/clo-etfs-nesting-doll">
              CLO ETFs — The Waterfall, the Yield Pick-Up, and the Nesting-Doll Funds
            </Link>.
          </p>
        </section>
      )}

      <FaqSection items={faqItems} />

      <h2>Track {ticker} in DiviDrip</h2>
      <p>
        Add <strong>{ticker}</strong> to your watchlist on{' '}
        <Link to="/dashboard">DiviDrip</Link> to monitor price, fundamentals, and
        Capital Quality Rank week-over-week. Built for long-term investors tracking
        both dividend and non-dividend names.
      </p>
      <ul>
        <li><Link to="/dashboard">Open DiviDrip</Link> — search <strong>{ticker}</strong> and add it to your watchlist.</li>
        <li><Link to="/screener">Stock Screener</Link> — filter by sector, market cap, and Capital Quality Rank.</li>
        <li><Link to="/learn/how-to-screen-for-dividend-stocks">How to use the Screener</Link> — sector / quality / market-cap filters explained.</li>
      </ul>

      <p className="text-xs text-muted-foreground italic mt-6">
        Data refreshed {stock.fetched_at?.slice(0, 10) || 'recently'}. Not investment
        advice. Prices change continuously; always verify the latest numbers
        in the live app before buying.
      </p>
    </>
  );
}

function Row({ label, value }) {
  return (
    <tr className="border-b border-border/40">
      <td className="py-2 pr-3 text-muted-foreground">{label}</td>
      <td className="py-2 font-mono font-semibold text-right">{value}</td>
    </tr>
  );
}

// Round P153 (Sep 2026) — SPAC lifecycle section for blank-check
// shells. Self-fetches /spac-lifecycle (populated by the weekly Friday
// EDGAR sweep) and renders the stage, the de-SPAC new ticker, and the
// SEC filing trail. Renders the static explainer even before the
// first scan so the SEO copy is always present.
function SpacSeoSection({ ticker, stockName }) {
  const [life, setLife] = useState(null);
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/stocks/${ticker}/spac-lifecycle`)
      .then((r) => { if (!cancelled) setLife(r.data); })
      .catch(() => { /* soft-fail — static copy still renders */ });
    return () => { cancelled = true; };
  }, [ticker]);

  const stage = life?.stage;
  if (life?.role === 'successor') {
    return (
      <>
        <h2>De-SPAC origin</h2>
        <p data-testid="stock-seo-spac-successor">
          <strong>{ticker}</strong> ({stockName}) came public through a SPAC merger:
          the blank-check shell <Link to={`/stock/${life.predecessor_ticker}`}><strong>{life.predecessor_ticker}</strong></Link>{life.predecessor_name ? ` (${life.predecessor_name})` : ''} merged
          with <strong>{life.deal_target || 'its target company'}</strong> and the combined
          company now trades under the {ticker} ticker. Market-data providers can lag
          on renaming the new entity — the SEC filings below describe the new business.
          Learn how de-SPACs work in our{' '}
          <Link to="/learn/spac-lifecycle">SPAC Lifecycle guide</Link>.
        </p>
        {Array.isArray(life?.evidence) && life.evidence.length > 0 && (
          <table className="w-full text-sm my-4 border-collapse" data-testid="stock-seo-spac-filings">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-2 pr-3 font-semibold">SEC filing</th>
                <th className="text-left py-2 pr-3 font-semibold">Date</th>
                <th className="text-left py-2 font-semibold">Milestone</th>
              </tr>
            </thead>
            <tbody>
              {life.evidence.slice(0, 5).map((ev, i) => (
                <tr key={i} className="border-b border-border/40">
                  <td className="py-2 pr-3 font-mono text-xs">
                    {ev.url ? (
                      <a href={ev.url} target="_blank" rel="noopener noreferrer" className="text-violet-600 dark:text-violet-400 hover:underline">
                        {ev.form}
                      </a>
                    ) : ev.form}
                  </td>
                  <td className="py-2 pr-3 font-mono text-xs">{ev.date || '—'}</td>
                  <td className="py-2 text-xs text-muted-foreground">{ev.note}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </>
    );
  }
  return (
    <>
      <h2>SPAC status</h2>
      <p data-testid="stock-seo-spac-intro">
        <strong>{ticker}</strong> ({stockName}) is a <strong>Special Purpose
        Acquisition Company</strong> — a blank-check shell that holds its IPO cash in a
        trust account while searching for a private company to merge with. SPACs pay no
        dividends. Learn how the whole arc works in our{' '}
        <Link to="/learn/spac-lifecycle">SPAC Lifecycle guide</Link>.
      </p>
      {life?.stage_label && stage !== 'unknown' && (
        <p data-testid="stock-seo-spac-stage">
          {life.renamed_from ? (
            <>Formerly traded as <strong>{life.renamed_from}</strong> — same SPAC, renamed ahead of its pending merger.{' '}</>
          ) : null}
          Current lifecycle stage per SEC EDGAR: <strong>{life.stage_label}</strong>
          {stage === 'ticker_changed' && (
            <>
              {' '}— this SPAC renamed its symbol{life.new_ticker ? <> to <Link to={`/stock/${life.new_ticker}`}><strong>{life.new_ticker}</strong></Link></> : null} ahead
              of its pending business combination{life.deal_target ? <> with <strong>{life.deal_target}</strong></> : null}.
              It is the same shell — the merger has not closed yet, and the {ticker} symbol is retired.
            </>
          )}
          {(stage === 'deal_announced' || stage === 'vote_pending') && life.deal_target && (
            <> — announced merger target: <strong>{life.deal_target}</strong>.</>
          )}
          {stage === 'completed' && life.surviving_same_ticker && (
            <> — the merger closed and the post-merger operating company continues to trade under the {ticker} ticker.</>
          )}
          {stage === 'completed' && !life.surviving_same_ticker && life.new_ticker && (
            <>
              {' '}— the shell merged with {life.deal_target ? <strong>{life.deal_target}</strong> : 'its target'} and the combined company now trades as{' '}
              <Link to={`/stock/${life.new_ticker}`}><strong>{life.new_ticker}</strong></Link>.
              The {ticker} ticker is retired.
            </>
          )}
          {stage === 'liquidated' && (
            <> — this SPAC never closed a deal. It was delisted and the trust was returned to shareholders.</>
          )}
          {life.checked_at && (
            <span className="text-muted-foreground"> (last EDGAR check {String(life.checked_at).slice(0, 10)})</span>
          )}
        </p>
      )}
      {Array.isArray(life?.evidence) && life.evidence.length > 0 && (
        <table className="w-full text-sm my-4 border-collapse" data-testid="stock-seo-spac-filings">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-2 pr-3 font-semibold">SEC filing</th>
              <th className="text-left py-2 pr-3 font-semibold">Date</th>
              <th className="text-left py-2 font-semibold">Milestone</th>
            </tr>
          </thead>
          <tbody>
            {life.evidence.slice(0, 5).map((ev, i) => (
              <tr key={i} className="border-b border-border/40">
                <td className="py-2 pr-3 font-mono text-xs">
                  {ev.url ? (
                    <a href={ev.url} target="_blank" rel="noopener noreferrer" className="text-violet-600 dark:text-violet-400 hover:underline">
                      {ev.form}
                    </a>
                  ) : ev.form}
                </td>
                <td className="py-2 pr-3 font-mono text-xs">{ev.date || '—'}</td>
                <td className="py-2 text-xs text-muted-foreground">{ev.note}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

// Round 95p·7 (Feb 2026) — Visible FAQ section mirroring the FAQPage
// JSON-LD. Uses native <details> so it works without any JS or
// external accordion dependency. All questions start collapsed
// except the first, which nudges the user to interact.
function FaqSection({ items }) {
  if (!items || items.length === 0) return null;
  return (
    <>
      <h2>Frequently asked</h2>
      <div className="space-y-2 my-4" data-testid="stock-seo-faq">
        {items.map((item, i) => (
          <details
            key={i}
            open={i === 0}
            className="group border rounded-lg px-4 py-3 bg-card/50 hover:bg-card transition-colors"
            data-testid={`stock-seo-faq-item-${i}`}
          >
            <summary className="cursor-pointer font-semibold text-sm list-none flex items-start justify-between gap-3">
              <span>{item.q}</span>
              <span className="text-muted-foreground text-lg leading-none select-none group-open:rotate-45 transition-transform" aria-hidden>+</span>
            </summary>
            <p className="text-sm text-muted-foreground leading-relaxed mt-2">{item.a}</p>
          </details>
        ))}
      </div>
    </>
  );
}

// Round 95p·8 (Feb 2026) — Related tickers card. Renders up to 4
// peer companies so readers keep exploring. Data comes from Massive's
// /v1/related-companies. Feb 2026 — the source ticker's dividend
// status drives which peer set we surface: dividend pages show
// related dividend payers with their yields; non-dividend pages show
// related non-dividend companies with their Capital Quality Rank.
// Silent no-render when the list is empty (fresh ticker or Massive
// coverage gap).
function RelatedTickers({ ticker, items, mode = 'dividend' }) {
  if (!items || items.length === 0) return null;
  const isNonDiv = mode === 'non_dividend';
  const heading = isNonDiv ? 'Related non-dividend companies' : 'Related dividend payers';
  const blurb = isNonDiv
    ? `Other non-dividend stocks investors research alongside ${ticker}. Peer set from Massive news + returns similarity, filtered to names we currently track.`
    : `Other dividend-paying stocks investors research alongside ${ticker}. Peer set from Massive news + returns similarity, filtered to names we currently track.`;
  return (
    <>
      <h2>{heading}</h2>
      <p className="text-sm text-muted-foreground">
        {blurb}
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 my-4" data-testid="stock-seo-related">
        {items.map((p) => (
          <Link
            key={p.ticker}
            to={`/stock/${p.ticker}`}
            className="border rounded-lg px-4 py-3 hover:border-primary/50 hover:bg-card/70 transition-colors flex items-start justify-between gap-3"
            data-testid={`stock-seo-related-${p.ticker}`}
          >
            <div className="min-w-0">
              <div className="flex items-center gap-1.5">
                <span className="font-mono font-semibold text-sm">{p.ticker}</span>
                {p.peer_source === 'sector_fallback' ? (
                  <span
                    className="text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-700 dark:text-amber-300 border border-amber-500/30"
                    title="Same-sector top-quality backfill — Massive didn't return this ticker as a direct peer."
                    data-testid={`stock-seo-related-${p.ticker}-source-sector`}
                  >
                    sector
                  </span>
                ) : (
                  <span
                    className="text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-sky-500/15 text-sky-700 dark:text-sky-300 border border-sky-500/30"
                    title="Direct peer from Massive's related-companies feed — investors research these together."
                    data-testid={`stock-seo-related-${p.ticker}-source-peer`}
                  >
                    peer
                  </span>
                )}
              </div>
              <div className="text-xs text-muted-foreground truncate">
                {p.name}
                {p.sector ? ` · ${p.sector}` : ''}
              </div>
            </div>
            {isNonDiv
              ? (typeof p.capital_quality_rank === 'number' && (
                  <div
                    className="text-xs font-mono font-semibold text-violet-600 dark:text-violet-300 shrink-0"
                    title="Capital Quality Rank (1-100)"
                  >
                    {p.capital_quality_rank.toFixed(0)}/100
                  </div>
                ))
              : (typeof p.dividend_yield === 'number' && (
                  <div className="text-xs font-mono font-semibold text-emerald-600 dark:text-emerald-300 shrink-0">
                    {p.dividend_yield.toFixed(2)}%
                  </div>
                ))}
          </Link>
        ))}
      </div>
    </>
  );
}

function fmtMarketCap(v) {
  const n = Number(v);
  if (!Number.isFinite(n) || n <= 0) return '—';
  if (n >= 1e12) return `$${(n / 1e12).toFixed(2)}T`;
  if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(0)}M`;
  return `$${n.toLocaleString()}`;
}
